import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DEFAULT_THEME_PREFERENCE,
  coerceThemePreference,
  type ThemeFamily,
  type ThemeMode,
  type ThemePreference,
} from "@jackedlogs/shared";

// Shared theme-preference context (one abstraction, both apps). It holds ONLY
// the persisted preference (palette family + light/dark mode) + setters; it does
// NOT resolve the dark flag — each platform's consumer hook (mobile useTheme,
// web-rnw useDark/useThemePalette) still owns its own useColorScheme() read and
// combines it with `mode` via resolveDark(). That keeps every existing consumer
// callsite unchanged: with no provider mounted (bare/test render) the hooks fall
// back to today's behaviour (OS scheme + brand family).
//
// Persistence + Profile bridging are injected by the host (AsyncStorage vs
// localStorage, Profile fetch/write differ per platform), so this component
// stays platform-agnostic — see the host wiring in each App root.

type ThemeContextValue = {
  preference: ThemePreference;
  // User actions — mark the preference "touched" (a late remote/cache read won't
  // clobber the live choice) and fire onChange so the host persists it.
  setFamily: (family: ThemeFamily) => void;
  setMode: (mode: ThemeMode) => void;
  setPreference: (pref: ThemePreference) => void;
  // Adopt a value fetched from Profile (cross-device source of truth). NOT a
  // user action: skipped once the user has picked this session; warms the cache
  // via onChange but never overrides a live choice. A bridge mounted where the
  // Profile is available calls this when the doc loads.
  syncRemote: (pref: Partial<ThemePreference> | null | undefined) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const samePref = (a: ThemePreference, b: ThemePreference) =>
  a.family === b.family && a.mode === b.mode;

export type ThemeProviderProps = {
  // Synchronously-or-early available seed (web: localStorage read at module
  // scope; mobile: an AsyncStorage read that lands shortly after mount). Applied
  // only until the user picks something this session. Pass a stable value.
  initialPreference?: Partial<ThemePreference> | null;
  // Host persists the new preference to its LOCAL CACHE (AsyncStorage /
  // localStorage). Fire-and-forget. Profile persistence is the picker's job, not
  // this callback's — keep the two writes separate so a remote sync only warms
  // the cache and never PUTs the Profile back.
  onChange?: (pref: ThemePreference) => void;
  children: React.ReactNode;
};

export function ThemeProvider({
  initialPreference,
  onChange,
  children,
}: ThemeProviderProps) {
  const [pref, setPref] = useState<ThemePreference>(() =>
    coerceThemePreference(initialPreference ?? DEFAULT_THEME_PREFERENCE),
  );
  // Once the user changes it this session, a late remote read (syncRemote)
  // must not clobber the live choice.
  const touched = useRef(false);

  // The seed lands on the first frame via the useState initializer above — both
  // hosts hand us a stable initialPreference (mobile holds first paint until its
  // cache read settles; web freezes it in a useState initializer), so there is
  // no post-mount re-seed to do. Cross-device values arrive through syncRemote.

  const value = useMemo<ThemeContextValue>(() => {
    const applyUser = (next: ThemePreference) => {
      touched.current = true;
      setPref(next);
      onChange?.(next);
    };
    return {
      preference: pref,
      setFamily: (family) => applyUser({ ...pref, family }),
      setMode: (mode) => applyUser({ ...pref, mode }),
      setPreference: applyUser,
      syncRemote: (remote) => {
        if (touched.current || remote == null) return;
        const next = coerceThemePreference({ ...pref, ...remote });
        if (samePref(pref, next)) return;
        setPref(next);
        onChange?.(next); // warm the cache with the cross-device value
      },
    };
  }, [pref, onChange]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Read the theme-preference context. Returns null when no ThemeProvider is
 * mounted (isolated/test renders) — consumer hooks treat null as "OS scheme +
 * brand family", i.e. the pre-theming behaviour.
 */
export function useThemePreferenceContext(): ThemeContextValue | null {
  return useContext(ThemeContext);
}
